/**
 * SignupForm — same pattern as LoginForm. The password field shows a
 * helper line spelling out the policy (14+ chars, upper/lower/digit/
 * symbol) so users hit fewer 400s.
 */
import { type SignupRequest, signupRequestSchema } from '@chaos/types/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ApiError, authApi } from '../../lib/api';
import { useAuthStore } from '../../stores/auth-store';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { useToaster } from '../ui/toast';

export function SignupForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toaster = useToaster();
  const setSession = useAuthStore((s) => s.setSession);

  const form = useForm<SignupRequest>({
    resolver: zodResolver(signupRequestSchema),
    defaultValues: { email: '', password: '', displayName: '' },
    mode: 'onBlur',
  });

  const signup = useMutation({
    mutationFn: authApi.signup,
    onSuccess: async (resp) => {
      try {
        const me = await authApi.me();
        setSession(me);
      } catch {
        setSession({
          user: resp.user,
          workspaces: [{ workspace: resp.workspace, membership: resp.membership }],
        });
      }
      toaster.toast({
        variant: 'success',
        title: t('app.name'),
        description: `Welcome, ${resp.user.displayName ?? resp.user.email}`,
      });
      void navigate({ to: '/dashboard' });
    },
    onError: (err) => {
      const message =
        err instanceof ApiError
          ? t(`auth.errors.${err.code}`, t('auth.errors.unknown'))
          : t('auth.errors.network');
      form.setError('root', { message });
      toaster.toast({ variant: 'destructive', title: message });
    },
  });

  const onSubmit = (values: SignupRequest) => {
    void signup.mutate(values);
  };

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        {form.formState.errors.root && (
          <Alert variant="destructive">
            <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
          </Alert>
        )}

        <FormField
          control={form.control}
          name="displayName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('auth.signup.display_name_label')}</FormLabel>
              <FormControl>
                <Input
                  autoComplete="name"
                  placeholder={t('auth.signup.display_name_placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('auth.signup.email_label')}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder={t('auth.signup.email_placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('auth.signup.password_label')}</FormLabel>
              <FormControl>
                <Input type="password" autoComplete="new-password" {...field} />
              </FormControl>
              <FormDescription>{t('auth.signup.password_help')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={signup.isPending}>
          {signup.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {signup.isPending ? t('auth.signup.submitting') : t('auth.signup.submit')}
        </Button>
      </form>
    </Form>
  );
}
